import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@invent/wl-ui-kit-next/ui';
import { ArrowRight, Plus } from 'lucide-react';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { DataTable, SplitList, StatusChip, type DataTableState, type IDataColumn } from '@/design';
import { formatDateTime } from '@/domains/survey';
import type { IExportPreset } from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import type { IAdminTeamFirm } from '../../api/team-firm-contracts';
import { AdminPage, LoadError, useAdminData } from '../../components/admin-page';
import { ListArea, useListRereads, usePaneRead, wholePage } from '../../components/list-area';
import type { ICardLinkState } from '../../components/participant-link';
import { SEEDED_MARK, listSurveyLabel } from '../../lists';
import { TEAM_PATHS, exportLayoutPath } from '../../paths';
import {
  LayoutForm,
  emptyLayout,
  firmLabel,
  layoutBody,
  layoutRefusal,
  useTeamFirms,
  type ILayoutDraft,
  type LayoutProblems
} from './layout';

/**
 * Setup → Export layouts (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md`
 * §6) — the files Veritant sends a firm and reads back, by layout: every export preset survey-api
 * holds, `GET /admin/export-presets`, behind `firm_exchange.manage` — the screen's gate and the
 * rail's, its reads included (the BrokerCheck refresh's precedent: Setup is where it is configured).
 * A layout opens beside the list (`AdminExportLayoutsArea`), where it is edited and deleted.
 *
 * **The list is survey-api's, whole and in its order** — unpaged, as the route answers it: Name ·
 * Survey ("Any survey" for none) · Firm (the register's label, else its code) · Columns · Updated,
 * a seed marked. Nothing here sorts or counts but the columns of each.
 *
 * **New layout** is a form above the list, opened by its button; a layout made opens beside it.
 * SR-402 AC4's *Contender standard set* is survey-api's seed, edited here like any other —
 * Veritant's example files become its headings, by hand.
 */
export function AdminExportLayoutsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const rereads = useListRereads();
  const [creating, setCreating] = React.useState(false);
  const firms = useTeamFirms();
  const { data, error, reload } = useAdminData(
    async (signal) => (await listsApi.presets({}, signal)) ?? [],
    [rereads]
  );
  const deleted = (location.state as Nullable<{ deletedLayout?: string }>)?.deletedLayout;
  const openLayout = (presetId: string) =>
    navigate(exportLayoutPath(presetId), { state: { search: '', list: true } satisfies ICardLinkState });

  return (
    <AdminPage
      title="Export layouts"
      crumb="Setup"
      actions={
        !creating && (
          <Button type="button" size="sm" onClick={() => setCreating(true)}>
            <Plus className="size-4" aria-hidden />
            New layout
          </Button>
        )
      }
    >
      {deleted && (
        <Alert>
          <AlertDescription>The layout {deleted} was deleted.</AlertDescription>
        </Alert>
      )}
      {creating && (
        <NewLayoutCard
          firms={firms}
          onCancel={() => setCreating(false)}
          onCreated={(presetId) => {
            setCreating(false);
            void reload();
            openLayout(presetId);
          }}
        />
      )}
      <p className="max-w-3xl text-sm text-muted-foreground">
        A layout is the columns and headings of a file a firm expects. An export written with it
        and a file read back by it match field to heading; Internal ID is always the first column.
      </p>
      {error && data !== null && (
        <Alert variant="destructive">
          <AlertTitle>Could not refresh — showing the layouts as last read</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <DataTable
        label="Export layouts"
        columns={layoutColumns(firms.data)}
        rows={data === null || data.length === 0 ? null : data}
        rowKey={(preset) => preset.id}
        onOpen={(preset) => openLayout(preset.id)}
        state={layoutsState(data === null, error)}
      />
    </AdminPage>
  );
}

function layoutColumns(firms: Nullable<IAdminTeamFirm[]>): IDataColumn<IExportPreset>[] {
  return [
    {
      id: 'name',
      header: 'Name',
      className: 'font-medium',
      cell: (preset) => (
        <span className="flex flex-wrap items-center gap-2">
          {preset.name}
          {preset.seed !== null && <StatusChip tone="neutral">{SEEDED_MARK}</StatusChip>}
        </span>
      )
    },
    {
      id: 'survey',
      header: 'Survey',
      cell: (preset) => (preset.survey ? listSurveyLabel(preset.survey) : 'Any survey')
    },
    {
      id: 'firm',
      header: 'Firm',
      cell: (preset) => (preset.firm ? firmLabel(firms, preset.firm) : <span className="text-muted-foreground">None</span>)
    },
    { id: 'columns', header: 'Columns', className: 'tabular-nums', cell: (preset) => preset.columns.length },
    { id: 'updated', header: 'Updated', cell: (preset) => formatDateTime(preset.updatedAt) },
    {
      id: 'open',
      header: '',
      label: 'Open',
      headClassName: 'w-10',
      cell: () => <ArrowRight className="size-4 text-muted-foreground" aria-hidden />
    }
  ];
}

/** `POST /admin/export-presets`, its refusals at the boxes they name; a layout made opens beside
 *  the list. */
function NewLayoutCard({
  firms,
  onCancel,
  onCreated
}: {
  firms: { data: Nullable<IAdminTeamFirm[]>; error: Nullable<string> };
  onCancel: () => void;
  onCreated: (presetId: string) => void;
}) {
  const [draft, setDraft] = React.useState<ILayoutDraft>(emptyLayout);
  const [problems, setProblems] = React.useState<LayoutProblems>({});
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const [saving, setSaving] = React.useState(false);

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setProblems({});
    setSaid(null);
    try {
      const made = await listsApi.createPreset(layoutBody(draft));
      onCreated(made.body.id);
    } catch (createError) {
      const refusal = layoutRefusal(createError, 'making the layout');
      setProblems(refusal.problems);
      setSaid(
        refusal.conflict
          ? 'The answer did not arrive — see whether the layout is in the list below before making it again.'
          : refusal.said
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-new-layout>
      <CardHeader>
        <CardTitle>New layout</CardTitle>
        <CardDescription>Its survey is fixed once it is made.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4" onSubmit={(event) => void create(event)} noValidate>
          <LayoutForm
            draft={draft}
            onDraft={setDraft}
            onReshape={() => setProblems({})}
            surveyFixed={false}
            problems={problems}
            firms={firms}
            disabled={saving}
          />
          {said && (
            <p role="alert" className="text-sm text-destructive">
              {said}
            </p>
          )}
          <div className="flex gap-2">
            <Button type="submit" disabled={saving}>
              {saving ? 'Making the layout…' : 'Make the layout'}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/** The layouts and a layout, as one area: the list, or a layout beside it. */
export function AdminExportLayoutsArea() {
  return (
    <ListArea
      param="presetId"
      list={TEAM_PATHS.exportLayouts}
      paneTitle="Export layouts"
      pane={(search, selected) => <LayoutsPane search={search} selected={selected} />}
    />
  );
}

/** The layouts table's state: the read out, failed, or answered with none. */
function layoutsState(unread: boolean, error: Nullable<string>): DataTableState {
  if (unread && error) return { kind: 'error', content: <LoadError message={error} /> };
  if (unread) return { kind: 'loading' };
  return { kind: 'empty', content: 'No export layout yet.' };
}

/** The layouts as the pane beside one draws them — one page, the whole list. */
function LayoutsPane({ search, selected }: { search: string; selected: string }) {
  const { shown, status } = usePaneRead(search, 'No export layout yet.', async (_params, _page, signal) => {
    return wholePage((await listsApi.presets({}, signal)) ?? []);
  });
  return (
    <SplitList
      label="Export layouts"
      selected={shown?.items.some((preset) => preset.id === selected) ? selected : null}
      items={
        shown?.items.map((preset) => ({
          key: preset.id,
          to: exportLayoutPath(preset.id),
          state: { search, list: true } satisfies ICardLinkState,
          primary: preset.name,
          secondary: preset.survey ? listSurveyLabel(preset.survey) : 'Any survey'
        })) ?? null
      }
      status={status}
    />
  );
}
